import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { hasHeadingLevelGap, hasTableOfContents, headings, numberedHeadings, readDocument } from "@/office/word/document";

export const lektionKapitelnummerierungAbschluss: Lesson = {
  id: "abschlussuebung",
  title: "Abschlussübung: Ein nummeriertes Handbuch",
  summary: "Nummerierung, Gliederung und Inhaltsverzeichnis greifen ineinander.",
  durationMinutes: 10,
  learningGoals: [
    "Eine mehrstufige, nummerierte Gliederung aufbauen",
    "Das Zusammenspiel mit dem Inhaltsverzeichnis überprüfen",
  ],
  steps: [
    {
      id: "handbuch",
      title: "Das Handbuch aufbauen",
      explanation: [
        "Baue die Gliederung eines kleinen Handbuchs: drei Hauptkapitel, jeweils mit " +
          "Unterkapiteln, durchgehend nummeriert – und darüber ein Inhaltsverzeichnis.",
        "Achte darauf, dass die Nummern im Inhaltsverzeichnis mit denen im Text " +
          "übereinstimmen. Falls nicht, hilft ein Klick ins Verzeichnis und F9.",
      ],
      keyPoints: [
        "Erst die Überschriften mit den richtigen Ebenen, dann nummerieren.",
        "Das Inhaltsverzeichnis übernimmt die Nummern automatisch.",
        "F9 aktualisiert das Verzeichnis.",
      ],
      task: {
        instruction:
          "Erstelle eine Gliederung mit mindestens drei Überschriften der Ebene 1 und " +
          "mindestens vier der Ebene 2, alle automatisch nummeriert, ohne Ebenensprünge " +
          "und mit einem Inhaltsverzeichnis.",
        hint: "Die Nummerierung weist du genau einmal zu – neue Überschriften übernehmen sie dann von selbst.",
        check: async () => {
          const snapshot = await readDocument();
          const alle = headings(snapshot);
          if (alle.length === 0) {
            return fail(
              "Im Dokument gibt es noch keine Überschriften.",
              "Weise zuerst die Überschriftenvorlagen zu.",
            );
          }

          const ebene1 = alle.filter((eintrag) => eintrag.level === 1);
          const ebene2 = alle.filter((eintrag) => eintrag.level === 2);
          const nummeriert = numberedHeadings(snapshot);

          return fromCriteria(
            [
              criterion(`Mindestens drei Überschriften der Ebene 1 (gefunden: ${ebene1.length})`, ebene1.length >= 3),
              criterion(`Mindestens vier Überschriften der Ebene 2 (gefunden: ${ebene2.length})`, ebene2.length >= 4),
              criterion("Keine übersprungene Ebene", !hasHeadingLevelGap(snapshot)),
              criterion(
                `Alle Überschriften sind nummeriert (${nummeriert.length} von ${alle.length})`,
                nummeriert.length === alle.length,
              ),
              criterion("Ein Inhaltsverzeichnis ist vorhanden", hasTableOfContents(snapshot)),
            ],
            {
              success: "Eine Gliederung, die sich beim nächsten neuen Kapitel selbst neu sortiert.",
              failure: "Fast geschafft. Diese Punkte fehlen noch:",
              hint: "Bleibt eine Überschrift ohne Nummer, gehört sie vermutlich zu einer zweiten, unabhängigen Liste. Rechtsklick → „Nummerierung fortsetzen“.",
            },
          );
        },
      },
    },
    {
      id: "rueckblick",
      title: "Rückblick",
      explanation: [
        "Die Kapitelnummerierung ist der Punkt, an dem die drei bisherigen Bausteine " +
          "zusammenkommen: Überschriftenvorlagen liefern die Struktur, die Liste mit " +
          "mehreren Ebenen liefert die Nummern, und das Inhaltsverzeichnis liest beides " +
          "aus.",
        "Wer bis hierhin sauber gearbeitet hat, kann in einem hundertseitigen Dokument " +
          "ein Kapitel an eine andere Stelle ziehen – und Nummerierung, Verzeichnis und " +
          "Querverweise stimmen danach immer noch.",
      ],
      keyPoints: [
        "Nummerierung genau einmal zuweisen, danach über die Vorlagen arbeiten.",
        "Bei unterbrochener Zählung: Rechtsklick → Nummerierung fortsetzen.",
        "Verzeichnisse mit F9 aktualisieren.",
      ],
    },
  ],
};
